import { Home, Building2, BedDouble, UtensilsCrossed, Warehouse, Crown, ToyBrick, Lamp } from 'lucide-react';

const CDN = 'https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images';

/* NYC skyline panorama — rendered as a CSS overlay inside window quads for the Night City ambience */
export const NIGHT_CITY_SKYLINE = `${CDN}/f1e31d2b2290b262273bd6a2889fbcfb75287244713b7041cbac63509a069ece.jpeg`;

/* Each shade quad = [tl, tr, br, bl] in % of the image, covering the full window (cassette bottom → sill).
   Rooms are photographed with the shade fully rolled up, so the fabric can be drawn at any drop 0..1.
   Ambience keys: day (daylight), night (warm interior). nightCity reuses the night photo + skyline overlay. */
/* Showroom reference rooms supplied by the client (real NYC exteriors). Images in /public/showroom.
   Photos were retouched so NO furniture/decor crosses the window glass: every shade can travel to 100%
   without any occlusion mask. Quads = glass measured by green-screen (memory/showroom/remeasure.py) then expanded to the
   OUTER edge of the dark window frame (frame_outset.py) — inside-jamb install: fabric overlaps the frame, adjacent shades
   split a shared mullion at its centre, no exposed strip. Day/night share identical quads. */
const P = 1264 / 848;
const sr = (id, shades) => ({ image: `/showroom/${id}.jpeg`, shades });

export const ROOM_SCENES = [
  {
    id: 'living', name: 'Living Room', icon: Home, aspect: P,
    variants: {
      day: sr('living2_day_cv', [
        { label: 'Picture Window', quad: [[18.5, 29.7], [81.7, 29.7], [81.7, 61.0], [18.5, 61.0]] },
      ]),
      night: sr('living2_night_cv', [
        { label: 'Picture Window', quad: [[18.5, 29.7], [81.7, 29.7], [81.7, 61.0], [18.5, 61.0]] },
      ]),
      nightCity: sr('living2_nightcity_cv', [
        { label: 'Picture Window', quad: [[18.5, 29.7], [81.7, 29.7], [81.7, 61.0], [18.5, 61.0]] },
      ]),
    },
  },
  {
    id: 'bedroom', name: 'Bedroom', icon: BedDouble, aspect: P,
    variants: {
      day: sr('bedroom_day_cv', [
        { label: 'Window', quad: [[26.9, 18.85], [77.14, 18.85], [77.14, 60.91], [26.9, 60.91]] },
      ]),
      night: sr('bedroom_night_cv', [
        { label: 'Window', quad: [[26.9, 18.85], [77.14, 18.85], [77.14, 60.91], [26.9, 60.91]] },
      ]),
      nightCity: sr('bedroom_nightcity_cv', [
        { label: 'Window', quad: [[26.9, 18.85], [77.14, 18.85], [77.14, 60.91], [26.9, 60.91]] },
      ]),
    },
  },
  {
    id: 'office', name: 'Home Office', icon: Building2, aspect: P,
    variants: {
      day: sr('office2_day_cv', [
        { label: 'Window', quad: [[33.8, 20.8], [66.2, 20.8], [66.2, 59.6], [33.8, 59.6]] },
      ]),
      night: sr('office2_night_cv', [
        { label: 'Window', quad: [[33.8, 20.8], [66.2, 20.8], [66.2, 59.6], [33.8, 59.6]] },
      ]),
      nightCity: sr('office2_nightcity_cv', [
        { label: 'Window', quad: [[33.8, 20.8], [66.2, 20.8], [66.2, 59.6], [33.8, 59.6]] },
      ]),
    },
  },
  {
    id: 'dining', name: 'Dining Room', icon: UtensilsCrossed, aspect: P,
    variants: {
      day: sr('dining_day_cv', [
        { label: 'Left Window', quad: [[3.53, 19.45], [33.72, 25.69], [33.74, 63.9], [3.81, 68.54]] },
        { label: 'Right Window', quad: [[40.09, 27.46], [56.37, 30.96], [56.37, 60.24], [39.91, 62.78]] },
      ]),
      night: sr('dining_night_v2_cv', [
        { label: 'Left Window', quad: [[3.53, 19.45], [33.72, 25.69], [33.74, 63.9], [3.81, 68.54]] },
        { label: 'Right Window', quad: [[40.09, 27.46], [56.37, 30.96], [56.37, 60.24], [39.91, 62.78]] },
      ]),
      nightCity: sr('dining_nightcity_cv', [
        { label: 'Left Window', quad: [[3.53, 19.45], [33.72, 25.69], [33.74, 63.9], [3.81, 68.54]] },
        { label: 'Right Window', quad: [[40.09, 27.46], [56.37, 30.96], [56.37, 60.24], [39.91, 62.78]] },
      ]),
    },
  },
  {
    id: 'loft', name: 'Urban Loft', icon: Warehouse, aspect: P,
    variants: {
      day: sr('loft3_day_cv', [
        { label: 'Factory Window', quad: [[18.75, 18.9], [81.37, 18.9], [81.37, 66.0], [18.75, 66.0]] },
      ]),
      night: sr('loft3_night_cv', [
        { label: 'Factory Window', quad: [[18.75, 18.9], [81.37, 18.9], [81.37, 66.0], [18.75, 66.0]] },
      ]),
      nightCity: sr('loft3_nightcity_cv', [
        { label: 'Factory Window', quad: [[18.75, 18.9], [81.37, 18.9], [81.37, 66.0], [18.75, 66.0]] },
      ]),
    },
  },
  {
    id: 'penthouse', name: 'Penthouse', icon: Crown, aspect: P,
    variants: {
      day: sr('penthouse2_day_cv', [
        { label: 'Left Panel', quad: [[5.1, 28.4], [34.96, 28.4], [34.96, 71.7], [5.1, 71.7]] },
        { label: 'Centre Panel', quad: [[34.96, 28.4], [65.27, 28.4], [65.27, 71.7], [34.96, 71.7]] },
        { label: 'Right Panel', quad: [[65.27, 28.4], [95.0, 28.4], [95.0, 71.7], [65.27, 71.7]] },
      ]),
      night: sr('penthouse2_night_cv', [
        { label: 'Left Panel', quad: [[5.1, 28.4], [34.96, 28.4], [34.96, 71.7], [5.1, 71.7]] },
        { label: 'Centre Panel', quad: [[34.96, 28.4], [65.27, 28.4], [65.27, 71.7], [34.96, 71.7]] },
        { label: 'Right Panel', quad: [[65.27, 28.4], [95.0, 28.4], [95.0, 71.7], [65.27, 71.7]] },
      ]),
      nightCity: sr('penthouse2_nightcity_cv', [
        { label: 'Left Panel', quad: [[5.1, 28.4], [34.96, 28.4], [34.96, 71.7], [5.1, 71.7]] },
        { label: 'Centre Panel', quad: [[34.96, 28.4], [65.27, 28.4], [65.27, 71.7], [34.96, 71.7]] },
        { label: 'Right Panel', quad: [[65.27, 28.4], [95.0, 28.4], [95.0, 71.7], [65.27, 71.7]] },
      ]),
    },
  },
  {
    id: 'kids', name: 'Kids Room', icon: ToyBrick, aspect: P,
    variants: {
      day: sr('kids_day_cv', [
        { label: 'Left Window', quad: [[15.6, 23.6], [42.4, 23.6], [42.4, 65.19], [15.6, 65.19]] },
        { label: 'Right Window', quad: [[57.6, 23.6], [84.2, 23.6], [84.2, 65.19], [57.6, 65.19]] },
      ]),
      night: sr('kids_night_cv', [
        { label: 'Left Window', quad: [[15.6, 23.6], [42.4, 23.6], [42.4, 65.19], [15.6, 65.19]] },
        { label: 'Right Window', quad: [[57.6, 23.6], [84.2, 23.6], [84.2, 65.19], [57.6, 65.19]] },
      ]),
      nightCity: sr('kids_nightcity_cv', [
        { label: 'Left Window', quad: [[15.6, 23.6], [42.4, 23.6], [42.4, 65.19], [15.6, 65.19]] },
        { label: 'Right Window', quad: [[57.6, 23.6], [84.2, 23.6], [84.2, 65.19], [57.6, 65.19]] },
      ]),
    },
  },
  {
    id: 'street', name: 'Street View', icon: Lamp, aspect: P, exterior: true,
    variants: {
      night: sr('street_facade_night', [
        { label: 'Floor 3 · 1', quad: [[16.2, 20.0], [24.4, 20.0], [24.4, 31.5], [16.2, 31.5]] },
        { label: 'Floor 3 · 2', quad: [[36.2, 20.0], [44.4, 20.0], [44.4, 31.5], [36.2, 31.5]] },
        { label: 'Floor 3 · 3', quad: [[56.2, 20.0], [64.4, 20.0], [64.4, 31.5], [56.2, 31.5]] },
        { label: 'Floor 3 · 4', quad: [[76.2, 20.0], [84.5, 20.0], [84.5, 31.5], [76.2, 31.5]] },
        { label: 'Floor 2 · 1', quad: [[16.2, 40.0], [24.4, 40.0], [24.4, 53.0], [16.2, 53.0]] },
        { label: 'Floor 2 · 2', quad: [[36.2, 40.0], [44.4, 40.0], [44.4, 53.0], [36.2, 53.0]] },
        { label: 'Floor 2 · 3', quad: [[56.2, 40.0], [64.4, 40.0], [64.4, 53.0], [56.2, 53.0]] },
        { label: 'Floor 2 · 4', quad: [[76.2, 40.0], [84.5, 40.0], [84.5, 53.0], [76.2, 53.0]] },
        { label: 'Floor 1 · 1', quad: [[36.2, 62.0], [44.4, 62.0], [44.4, 76.0], [36.2, 76.0]] },
        { label: 'Floor 1 · 2', quad: [[56.2, 62.0], [64.4, 62.0], [64.4, 76.0], [56.2, 76.0]] },
        { label: 'Floor 1 · 3', quad: [[76.2, 62.0], [84.5, 62.0], [84.5, 76.0], [76.2, 76.0]] },
      ]),
    },
  },
].map(scene => ({ ...scene, images: { day: (scene.variants.day || scene.variants.night).image, night: scene.variants.night?.image }, shades: (scene.variants.day || scene.variants.night).shades }));

/* Ambiences a scene can really show (no faked lighting): night only when a real night photo exists */
export const sceneAmbiences = (scene) => (scene?.variants ? ['day', 'night', 'nightCity'].filter(k => scene.variants[k]) : ['day', 'night', 'nightCity']);

/* Flatten a scene to the variant for the requested ambience (image, shades, foreground) */
export const resolveScene = (scene, ambience) => {
  if (!scene?.variants) return scene;
  const v = scene.variants[ambience] || scene.variants[ambience === 'nightCity' ? 'night' : ambience] || scene.variants.day || Object.values(scene.variants)[0];
  return { ...scene, images: { day: v.image, foreground: v.foreground }, shades: v.shades };
};

/* Previous stock rooms — kept for saved looks that reference them */
export const LEGACY_ROOM_SCENES = [
  {
    id: 'living',
    name: 'Living Room',
    icon: Home,
    aspect: 848 / 1264,
    images: {
      day: `${CDN}/3c561ac7b767ba669ce37d54fc77db7f8a3ae4d3c39eea74186ceebc9f0e9ef9.jpeg`,
      night: `${CDN}/24744fc0b24e9dee0160ff1ce92d137e54fabb8acca768f3de18f1db4a822ffe.jpeg`,
    },
    shades: [{ label: 'Window Shade', quad: [[31.96, 16.75], [68.28, 16.75], [68.28, 60.38], [31.96, 60.38]] }],
  },
  {
    id: 'office',
    name: 'Office',
    icon: Building2,
    aspect: 848 / 1264,
    images: {
      day: `${CDN}/16f765d3911a7aa578b3627c19b6b88515478282c491d9df0e8fb025cf4f5bd8.jpeg`,
      night: `${CDN}/30e48ac5ab7bbcfeaabd3cb363e528a918e4c197f0671ea5c4a6c10fe140d123.jpeg`,
    },
    shades: [{ label: 'Window Shade', quad: [[26.5, 13.21], [73.5, 13.21], [73.5, 58.73], [26.5, 58.73]] }],
  },
  {
    id: 'bedroom',
    name: 'Bedroom',
    icon: BedDouble,
    aspect: 848 / 1264,
    images: {
      day: `${CDN}/639c8a3b5c51fb869af650e5757c65e364700db32854a29a1e0a2a6853301b89.jpeg`,
      night: `${CDN}/be5cf737141f7cdce2ddf7dbabe5c34bde5c50656ea4242bd8a7362a0814af52.jpeg`,
    },
    shades: [{ label: 'Window Shade', quad: [[32.75, 17.92], [67.33, 17.92], [67.33, 54.36], [32.75, 54.36]] }],
  },
  {
    id: 'dining',
    name: 'Dining Room',
    icon: UtensilsCrossed,
    aspect: 848 / 1264,
    images: {
      day: `${CDN}/898caca4cf21e77e8f6f501fc90b744ba9cf846d3c874382e27bfc28f8607805.jpeg`,
      night: `${CDN}/dc66e5f2c5bb6d823888f0a256faed6d7c026fe438610538fc41f6cbbf26e407.jpeg`,
    },
    shades: [
      { label: 'Left Window', quad: [[7.52, 13.44], [42.41, 13.44], [42.41, 63.68], [7.52, 63.68]] },
      { label: 'Right Window', quad: [[57.59, 13.44], [92.72, 13.44], [92.72, 63.68], [57.59, 63.68]] },
    ],
  },
];

export const DEFAULT_CUSTOM_QUAD = [[25, 15], [75, 15], [75, 55], [25, 55]];

export const sceneImage = (scene, ambience) => {
  // nightCity uses the same room photo as night — the skyline is rendered as a CSS overlay
  const key = ambience === 'nightCity' ? 'night' : ambience;
  return scene.images?.[key] || scene.images?.day || scene.imageUrl;
};

/* Portion of the window quad covered when the shade is lowered by drop (0..1); a small sliver always shows under the cassette */
export const shadeQuad = (quad, drop) => {
  const d = Math.max(0.03, Math.min(1, drop));
  const [tl, tr, br, bl] = quad;
  return [tl, tr, [tr[0] + (br[0] - tr[0]) * d, tr[1] + (br[1] - tr[1]) * d], [tl[0] + (bl[0] - tl[0]) * d, tl[1] + (bl[1] - tl[1]) * d]];
};
